import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { MatBadge } from '@angular/material/badge';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatButtonToggle,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { TranslocoDatePipe } from '@jsverse/transloco-locale';
import { NotificationList } from '@layout/data/notification-list';
import {
  notificationIcon,
  NotificationsService,
} from '@layout/data/notifications';
import { NotificationsStreamService } from '@layout/data/notifications-stream';
import {
  BuiPopover,
  BuiPopoverClose,
  BuiPopoverContent,
  BuiPopoverPortal,
  BuiPopoverTrigger,
  PopoverPosition,
} from '@ui/popover';
import { BuiSkeleton, BuiSkeletonRepeat } from '@ui/skeleton';

@Component({
  selector: 'notifications',
  imports: [
    MatButton,
    MatIconButton,
    MatIcon,
    MatBadge,
    MatDivider,
    MatButtonToggle,
    MatButtonToggleGroup,
    RouterLink,
    MatTooltip,
    MatProgressSpinner,
    TranslocoDirective,
    TranslocoDatePipe,
    BuiPopover,
    BuiPopoverTrigger,
    BuiPopoverPortal,
    BuiPopoverContent,
    BuiPopoverClose,
    BuiSkeleton,
    BuiSkeletonRepeat,
  ],
  providers: [NotificationList],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container *transloco="let t">
      <div
        buiPopover
        class="w-full sm:max-w-md"
        [position]="position()"
        [closeOnScroll]="true"
        (opened)="onOpened()"
      >
        <!-- Trigger -->
        <button
          matIconButton
          buiPopoverTrigger
          [matBadge]="unreadTotal()"
          [matBadgeHidden]="!unreadTotal()"
          matBadgeSize="small"
        >
          <mat-icon [svgIcon]="unreadTotal() ? 'bell-dot' : 'bell'" />
        </button>

        <!-- Panel -->
        <ng-template buiPopoverPortal>
          <div
            buiPopoverContent
            class="p-0"
          >
            <!-- Header -->
            <div class="mx-6 mt-5 flex items-center gap-x-1">
              <div class="text-xl font-medium">
                {{ t('LAYOUT.NOTIFICATIONS.TITLE') }}
              </div>

              <div class="flex-auto"></div>

              <button
                matIconButton
                class="small"
                [matTooltip]="t('LAYOUT.NOTIFICATIONS.MARK_ALL_AS_READ')"
                [disabled]="!unreadTotal()"
                (click)="onMarkAllAsRead()"
              >
                <mat-icon svgIcon="check-check" />
              </button>
              <button
                matIconButton
                class="small"
                [matTooltip]="t('LAYOUT.NOTIFICATIONS.DELETE_ALL')"
                [disabled]="!state().total"
                (click)="onDeleteAll()"
              >
                <mat-icon svgIcon="trash-2" />
              </button>
              <a
                matIconButton
                buiPopoverClose
                class="small"
                routerLink="/settings/notifications"
                [matTooltip]="t('LAYOUT.NOTIFICATIONS.SETTINGS')"
              >
                <mat-icon svgIcon="settings-2" />
              </a>
              <button
                matIconButton
                buiPopoverClose
                class="small"
                [matTooltip]="t('LAYOUT.NOTIFICATIONS.CLOSE')"
              >
                <mat-icon svgIcon="x" />
              </button>
            </div>

            <!-- Filters -->
            <div class="mx-6 mt-3">
              <mat-button-toggle-group
                class="segmented"
                name="notificationReadState"
                hideSingleSelectionIndicator
                [aria-label]="t('LAYOUT.NOTIFICATIONS.READ_STATE')"
                [value]="filter().unreadOnly"
                (change)="onUnreadOnlyChange($event.value)"
              >
                <mat-button-toggle [value]="false">
                  {{ t('LAYOUT.NOTIFICATIONS.ALL') }}
                </mat-button-toggle>
                <mat-button-toggle [value]="true">
                  {{ t('LAYOUT.NOTIFICATIONS.UNREAD') }}
                  @if (unreadTotal()) {
                    <span
                      class="font-mono text-neutral-a11 slashed-zero tabular-nums"
                      >{{ unreadTotal() }}</span
                    >
                  }
                </mat-button-toggle>
              </mat-button-toggle-group>
            </div>

            <!-- Notifications -->
            <div
              class="flex min-h-88 flex-col gap-y-2 overflow-y-auto overscroll-contain px-6 pt-1 pb-4 sm:max-h-96"
            >
              @if (showSkeleton()) {
                <ng-template [buiSkeletonRepeat]="5">
                  <div class="flex w-full items-start gap-x-3 py-2">
                    <div
                      buiSkeleton
                      class="mt-1 size-6 shrink-0 rounded-lg"
                    ></div>
                    <div class="flex min-w-0 flex-auto flex-col gap-y-1.5">
                      <div
                        buiSkeleton
                        class="h-3.5 w-3/4"
                      ></div>
                      <div
                        buiSkeleton
                        class="h-3 w-full"
                      ></div>
                      <div
                        buiSkeleton
                        class="h-3 w-20"
                      ></div>
                    </div>
                  </div>
                </ng-template>
              } @else {
                @for (
                  notification of state().notifications;
                  track notification.id;
                  let last = $last
                ) {
                  <div class="flex w-full items-start gap-x-3 py-2">
                    <button
                      type="button"
                      buiPopoverClose
                      class="flex min-w-0 flex-auto items-start gap-x-3 text-start"
                      (click)="notificationsService.open(notification)"
                    >
                      <!-- Icon -->
                      <div
                        class="mt-1 flex size-6 shrink-0 items-center justify-center rounded-lg"
                        [class]="
                          notification.readAt
                            ? 'bg-neutral-a4 text-neutral-a11'
                            : 'bg-primary-a3 text-primary-11'
                        "
                      >
                        <mat-icon
                          [svgIcon]="icon(notification)"
                          class="size-3.5"
                        />
                      </div>

                      <div class="flex min-w-0 flex-auto flex-col">
                        <!-- Title -->
                        <div
                          class="truncate text-sm"
                          [class.font-semibold]="!notification.readAt"
                        >
                          {{ notification.title }}
                        </div>

                        <!-- Message -->
                        @if (notification.message) {
                          <div
                            class="text-xs"
                            [class.text-neutral-a11]="!!notification.readAt"
                          >
                            {{ notification.message }}
                          </div>
                        }

                        <!-- Date -->
                        <div class="mt-0.5 text-xs text-neutral-a11">
                          {{
                            notification.createdAt
                              | translocoDate
                                : { dateStyle: 'short', timeStyle: 'short' }
                          }}
                        </div>
                      </div>
                    </button>

                    <!-- Mark as read -->
                    @if (!notification.readAt) {
                      <button
                        matIconButton
                        class="small"
                        [matTooltip]="t('LAYOUT.NOTIFICATIONS.MARK_AS_READ')"
                        (click)="onMarkAsRead(notification.id)"
                      >
                        <mat-icon svgIcon="check" />
                      </button>
                    }

                    <!-- Delete -->
                    <button
                      matIconButton
                      class="small"
                      [matTooltip]="t('LAYOUT.NOTIFICATIONS.DELETE')"
                      (click)="onDelete(notification.id)"
                    >
                      <mat-icon svgIcon="trash-2" />
                    </button>
                  </div>

                  @if (!last) {
                    <mat-divider />
                  }
                } @empty {
                  <div class="py-6 text-center text-sm text-neutral-a11">
                    {{ t('LAYOUT.NOTIFICATIONS.EMPTY') }}
                  </div>
                }
              }

              @if (state().currentPage < state().lastPage) {
                <button
                  matButton
                  class="small tertiary mt-2 w-full"
                  (click)="onLoadMore()"
                  [showProgress]="state().loadingMore"
                >
                  <mat-progress-spinner
                    progressIndicator
                    mode="indeterminate"
                    diameter="16"
                    aria-label="Loading"
                    tabindex=""
                  />
                  {{ t('LAYOUT.NOTIFICATIONS.LOAD_MORE') }}
                </button>
              }
            </div>

            <!-- Footer -->
            <div class="border-t p-1">
              <a
                matButton
                buiPopoverClose
                class="link w-full"
                routerLink="/notifications"
              >
                {{ t('LAYOUT.NOTIFICATIONS.VIEW_ALL') }}
              </a>
            </div>
          </div>
        </ng-template>
      </div>
    </ng-container>
  `,
})
export class Notifications {
  // Dependencies
  protected readonly notificationsService = inject(NotificationsService);
  private readonly notificationsStreamService = inject(
    NotificationsStreamService
  );
  private readonly list = inject(NotificationList);

  // Inputs / Outputs
  readonly position = input<PopoverPosition>('bottom-end');

  // State
  protected readonly icon = notificationIcon;
  protected readonly filter = this.list.filter;
  protected readonly state = this.list.state;
  protected readonly unreadTotal = this.notificationsService.unreadCount;
  protected readonly showSkeleton = computed(() => {
    const state = this.state();
    return state.loading && state.notifications.length === 0;
  });

  protected onUnreadOnlyChange(unreadOnly: boolean) {
    this.list.setFilter({ unreadOnly });
  }

  protected onOpened() {
    this.list.reload();
    this.notificationsStreamService.ensurePermission();
  }

  protected onMarkAllAsRead() {
    this.notificationsService.markAllAsRead(this.filter());
  }

  protected onMarkAsRead(id: string) {
    this.notificationsService.setRead([id], true);
  }

  protected onDelete(id: string) {
    this.notificationsService.delete([id]);
  }

  protected onDeleteAll() {
    this.notificationsService.deleteAll(this.filter());
  }

  protected onLoadMore() {
    this.list.loadMore();
  }
}
